import React, { useState, KeyboardEvent, useRef } from "react";
import {
  TextField,
  Chip,
  FormControl,
  InputLabel,
  FormHelperText,
  Paper,
} from "@mui/material";
import { Search } from "@mui/icons-material";

interface MultiTagInputProps {
  readonly tags: string[];
  readonly onTagsChange: (tags: string[]) => void;
  readonly placeholder?: string;
  readonly label?: string;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly styles?: {};
}

/**
 * A reusable multi-tag input component that allows users to add and remove tags.
 *
 * Features:
 * - Add tags by pressing Enter
 * - Remove tags by clicking the X button or pressing Backspace when input is empty
 * - Prevents duplicate tags
 * - Customizable placeholder and label
 * - Supports disabled state and error handling
 *
 * @example
 * ```tsx
 * const [orderNumbers, setOrderNumbers] = useState<string[]>([]);
 *
 * <MultiTagInput
 *   tags={orderNumbers}
 *   onTagsChange={setOrderNumbers}
 *   label="Order Numbers"
 *   placeholder="Enter order numbers..."
 * />
 * ```
 *
 * @param tags - Array of current tags
 * @param onTagsChange - Callback when tags change
 * @param placeholder - Placeholder text for the input
 * @param label - Label for the input field
 * @param disabled - Whether the input is disabled
 * @param error - Whether to show error state
 * @param helperText - Helper text to display below the input
 */
export default function MultiTagInput({
  tags,
  onTagsChange,
  placeholder = "Enter values...",
  label = "Tags",
  disabled = false,
  error = false,
  helperText,
  styles,
}: MultiTagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const parseAndAddTags = (text: string) => {
    // Split by space, comma, or semicolon and filter out empty strings
    const newTags = text
      .split(/[\s,;]+/)
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    // Add only unique tags that don't already exist
    const uniqueNewTags = newTags.filter((tag) => !tags.includes(tag));
    if (uniqueNewTags.length > 0) {
      onTagsChange([...tags, ...uniqueNewTags]);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "," ||
      event.key === ";"
    ) {
      event.preventDefault();
      if (inputValue.trim()) {
        parseAndAddTags(inputValue);
        setInputValue("");
      }
    } else if (event.key === "Backspace" && !inputValue && tags.length > 0) {
      onTagsChange(tags.slice(0, -1));
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    onTagsChange(tags.filter((tag) => tag !== tagToDelete));
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");

    // Parse the pasted text and add tags
    parseAndAddTags(pastedText);

    // Clear the input after pasting
    setInputValue("");
  };

  const handleContainerClick = () => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  };

  // Determine label color based on error and focused states
  const getLabelColor = () => {
    if (error) return "error.main";
    if (focused) return "primary.main";
    return "text.secondary";
  };

  const getborderColor = () => {
    if (error) return "error.main";
    if (focused) return "primary.main";
    return "rgba(0, 0, 0, 0.23)";
  };

  return (
    <FormControl fullWidth error={error} disabled={disabled} sx={{ padding: "0px" }}>
      <InputLabel
        shrink={true}
        sx={{
          position: "absolute",
          color: getLabelColor(),
          backgroundColor: "background.paper",
          padding: "0 4px",
          left: "-4px",
          fontWeight: 400,
        }}
      >
        {label}
      </InputLabel>

      <Paper
        onClick={handleContainerClick}
        variant="outlined"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: 0.5,
          padding: "8px",
          borderColor: getborderColor(),
          borderWidth: focused ? 2 : 1,
          margin: focused ? "-1px" : 0,
          backgroundColor: disabled
            ? "action.disabledBackground"
            : "background.paper",
          cursor: disabled ? "default" : "text",
          transition: "border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
        }}
      >
        <Search
          sx={{
            color: "action.active",
            flexShrink: 0,
            alignSelf: "center",
            my: "auto",
            mx: "4px",
          }}
        />

        {tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            onDelete={disabled ? undefined : () => handleDeleteTag(tag)}
            size="small"
            variant="outlined"
            disabled={disabled}
            sx={{
              height: "23px",
              flexShrink: 0,
              "& .MuiChip-deleteIcon": {
                fontSize: "16px",
              },
            }}
          />
        ))}

        <TextField
          multiline
          minRows={1}
          maxRows={6}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={tags.length === 0 ? placeholder : ""}
          disabled={disabled}
          inputRef={inputRef}
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            flex: 1,
            minWidth: "120px",
            minHeight: "23px",
            height: "32px",
            "& .MuiInputBase-root": {
              padding: "0px",
            },
            ...styles,
          }}
        />
      </Paper>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
